/**
 * Pure history model for the sidepanel view router (ADR 0001): a browser-like
 * stack of Views with a cursor. Exactly one entry — `topEntry` — is mounted at
 * a time; back/forward move the cursor without dropping entries; pushing
 * truncates any forward entries. Entry keys are stable across back/forward, so
 * per-entry persistence (scroll, drafts) can be keyed on them; every push or
 * replace mints a fresh key.
 *
 * No React, no atoms — unit-tested in `view-history.test.ts`.
 */

export interface ViewHistoryEntry<V> {
  key: number
  view: V
}

export interface ViewHistory<V> {
  entries: Array<ViewHistoryEntry<V>>
  /** Cursor into `entries`; the entry at this index is the single mounted View. */
  index: number
  /** Monotonic key allocator, part of the state so the model stays pure. */
  nextKey: number
}

export const createViewHistory = <V>(rootView: V): ViewHistory<V> => ({
  entries: [{ key: 0, view: rootView }],
  index: 0,
  nextKey: 1,
})

export const topEntry = <V>(h: ViewHistory<V>): ViewHistoryEntry<V> => h.entries[h.index]

export const canGoBack = <V>(h: ViewHistory<V>): boolean => h.index > 0

/**
 * Whether applying `next` changes which entry is mounted — a navigation, as
 * opposed to a no-op (back at the root, jump to the current index) or an
 * in-place context refresh (same key). What the leave guard keys on.
 */
export const changesTopEntry = <V>(h: ViewHistory<V>, next: ViewHistory<V>): boolean =>
  topEntry(next).key !== topEntry(h).key

/**
 * A leave guard registered by the mounted view while it holds unsent edits
 * (e.g. the Action View).
 */
export interface ViewNavigationGuard {
  /** Entry key that registered the guard — only applies while its entry is top. */
  entryKey: number
  /**
   * Receives a deferred user navigation; call `proceed` to let it through.
   * `null` when the edits save themselves on leave (an inline title), so only
   * automatic navigation is held back.
   */
  confirm: ((proceed: () => void) => void) | null
}

export type GuardedNavigation =
  | { type: 'apply' }
  | { type: 'skip' }
  | { type: 'confirm'; confirm: NonNullable<ViewNavigationGuard['confirm']> }

/**
 * What a leave guard makes of one navigation. Only a move off the guarded
 * entry while it's on top is guarded: an automatic one is skipped outright, and
 * a user one goes to `confirm` — unless the guard has none, or the surface is
 * hidden (a hide-time `closeAllViews` must never pop a dialog in a hidden
 * panel), in which case it applies.
 */
export const resolveGuardedNavigation = <V>(
  h: ViewHistory<V>,
  next: ViewHistory<V>,
  guard: ViewNavigationGuard | null,
  { automatic, surfaceShowing }: { automatic: boolean; surfaceShowing: boolean },
): GuardedNavigation => {
  if (guard === null || guard.entryKey !== topEntry(h).key || !changesTopEntry(h, next)) return { type: 'apply' }
  if (automatic) return { type: 'skip' }
  return guard.confirm !== null && surfaceShowing ? { type: 'confirm', confirm: guard.confirm } : { type: 'apply' }
}

export const canGoForward = <V>(h: ViewHistory<V>): boolean => h.index < h.entries.length - 1

/** Entries above the cursor, nearest first excluded — ordered root-outward. */
export const forwardViews = <V>(h: ViewHistory<V>): Array<ViewHistoryEntry<V>> => h.entries.slice(h.index + 1)

export const pushView = <V>(h: ViewHistory<V>, view: V): ViewHistory<V> => ({
  entries: [...h.entries.slice(0, h.index + 1), { key: h.nextKey, view }],
  index: h.index + 1,
  nextKey: h.nextKey + 1,
})

export const goBack = <V>(h: ViewHistory<V>): ViewHistory<V> => (canGoBack(h) ? { ...h, index: h.index - 1 } : h)

export const goForward = <V>(h: ViewHistory<V>): ViewHistory<V> => (canGoForward(h) ? { ...h, index: h.index + 1 } : h)

export const backToRoot = <V>(h: ViewHistory<V>): ViewHistory<V> => (canGoBack(h) ? { ...h, index: 0 } : h)

/**
 * Move the cursor to an arbitrary entry — the breadcrumb menus' jump. Unlike
 * `truncateTo` nothing is dropped, so this is many steps of back/forward at
 * once and the trail on both sides survives. Out-of-range indices are ignored
 * rather than clamped: a stale index from a menu rendered against an older
 * history should do nothing, not silently land somewhere else.
 */
export const jumpTo = <V>(h: ViewHistory<V>, index: number): ViewHistory<V> =>
  index < 0 || index >= h.entries.length ? h : { ...h, index }

/** Swap the current entry for a new View (fresh key); forward history survives, like `history.replaceState`. */
export const replaceTopView = <V>(h: ViewHistory<V>, view: V): ViewHistory<V> => ({
  entries: h.entries.map((entry, i) => (i === h.index ? { key: h.nextKey, view } : entry)),
  index: h.index,
  nextKey: h.nextKey + 1,
})

/** Refresh a View's payload in place, keeping the entry key (context updates, not navigation). */
export const updateViewAt = <V>(h: ViewHistory<V>, index: number, view: V): ViewHistory<V> => {
  if (index < 0 || index >= h.entries.length) {
    return h
  }
  return {
    ...h,
    entries: h.entries.map((entry, i) => (i === index ? { key: entry.key, view } : entry)),
  }
}

/** Views the by-id helpers below can address. */
interface IdentifiedView {
  id: string
}

/**
 * Patch the mounted view in place, keeping its entry key — the safe form of a
 * context refresh for a fact the open view learned *about itself*. `id` guards
 * against an effect landing after the user navigated on; a mismatch is a no-op.
 *
 * Deliberately the only by-id patcher: writing to every entry sharing the id
 * would stamp one document's payload onto another's entry whenever two of the
 * same kind are stacked (two chats, two meetings).
 */
export const updateTopView = <V extends IdentifiedView>(
  h: ViewHistory<V>,
  id: string,
  update: (view: V) => V,
): ViewHistory<V> => {
  const entry = topEntry(h)
  return entry.view.id === id ? updateViewAt(h, h.index, update(entry.view)) : h
}

/**
 * Open a view: push it onto the stack, so drill-ins stack and Back walks them
 * in reverse. The one exception is the *same document* already open below the
 * cursor — navigate back to it with a refreshed payload, keeping its entry key
 * so per-entry state (scroll, drafts) survives the refresh.
 *
 * Identity is the key, deliberately not the view id: two chats are two
 * documents even though they share the `chat` id, and collapsing one onto the
 * other would erase the trail between them *and* hand the newcomer the previous
 * chat's scroll offset and unsent draft (both keyed on entry key).
 *
 * The incoming open wins on a collapse: it carries the intent for *this* open
 * (which tab, which message), and nothing the mounted view resolved about
 * itself lives here to be overwritten. That belongs to whatever resolves it
 * (for meetings, `meeting-resolution`), not to a navigation entry. The one
 * exception is state the user made on the entry, like a chat thread opened over
 * it, which `reopen` carries onto the incoming view.
 */
export const openViewInHistory = <V>(
  h: ViewHistory<V>,
  view: V,
  identify: (view: V) => string,
  reopen: (current: V, incoming: V) => V = (_, incoming) => incoming,
): ViewHistory<V> => {
  const identity = identify(view)
  // Skip the root: Home is never a drill-in target.
  const existing = h.entries.findIndex((entry, i) => i > 0 && i <= h.index && identify(entry.view) === identity)
  return existing === -1
    ? pushView(h, view)
    : updateViewAt(truncateTo(h, existing), existing, reopen(h.entries[existing].view, view))
}

/**
 * Swap the open view with `id` for another, dropping everything above it so
 * the replacement becomes the top while its parents stay reachable via back.
 * Falls back to a push off the root when no open view has that id.
 *
 * Searched nearest-first from the cursor: callers are mounted views handing off
 * to another, so a same-id entry buried further down is a *different* document
 * (two chats stacked) and swapping that one would drop the trail above it.
 */
export const replaceViewById = <V extends IdentifiedView>(h: ViewHistory<V>, id: string, view: V): ViewHistory<V> => {
  let target = -1
  for (let i = h.index; i >= 0; i--) {
    if (h.entries[i].view.id === id) {
      target = i
      break
    }
  }
  return pushView(truncateTo(h, target === -1 ? 0 : target - 1), view)
}

/** Discard the forward trail, keeping the cursor where it is. */
export const dropForwardEntries = <V>(h: ViewHistory<V>): ViewHistory<V> =>
  canGoForward(h) ? truncateTo(h, h.index) : h

/** Drop everything above `index` and land on it. Clamped so the root always survives. */
export const truncateTo = <V>(h: ViewHistory<V>, index: number): ViewHistory<V> => {
  const clamped = Math.max(0, Math.min(index, h.entries.length - 1))
  return {
    entries: h.entries.slice(0, clamped + 1),
    index: clamped,
    nextKey: h.nextKey,
  }
}
